import { Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

// Enable detailed logging for debugging
Log.setLogger(console);
Log.setLevel(Log.DEBUG);

export interface OidcConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  response_type?: string;
  scope?: string;
  automaticSilentRenew?: boolean;
  silent_redirect_uri?: string;
}

export class OidcAuthService {
  private userManager: UserManager;
  private config: OidcConfig;

  constructor(config: OidcConfig) {
    this.config = config;

    const settings: UserManagerSettings = {
      authority: config.authority,
      client_id: config.client_id,
      redirect_uri: config.redirect_uri,
      post_logout_redirect_uri: config.post_logout_redirect_uri,
      response_type: config.response_type || 'code',
      scope: config.scope || 'openid profile email roles',

      // PKCE is automatically enabled for authorization code flow
      // Use session storage for better security
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),

      // Token management
      automaticSilentRenew: config.automaticSilentRenew ?? true,
      silent_redirect_uri: config.silent_redirect_uri || `${window.location.origin}/silent-renew.html`,
      accessTokenExpiringNotificationTimeInSeconds: 60,

      // Security settings
      filterProtocolClaims: false, // Keep role claims and other important claims
      loadUserInfo: true,
      monitorSession: true,

      // Response handling
      response_mode: 'query' as any,
    };

    this.userManager = new UserManager(settings);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    // User loaded event
    this.userManager.events.addUserLoaded((user) => {
      console.log('User loaded:', user.profile);
      this.onUserLoaded(user);
    });

    // User unloaded event
    this.userManager.events.addUserUnloaded(() => {
      console.log('User unloaded');
      this.onUserUnloaded();
    });

    // Access token expired
    this.userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired');
      this.onAccessTokenExpired();
    });

    // Access token expiring (60 seconds before expiration)
    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring soon');
      this.onAccessTokenExpiring();
    });

    // Silent renew error
    this.userManager.events.addSilentRenewError((error) => {
      console.error('Silent renew error:', error);
      this.onSilentRenewError(error);
    });

    // User signed out
    this.userManager.events.addUserSignedOut(() => {
      console.log('User signed out');
      this.onUserSignedOut();
    });
  }

  // Public methods
  async login(): Promise<void> {
    try {
      await this.userManager.signinRedirect();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async completeLogin(): Promise<User> {
    try {
      const user = await this.userManager.signinRedirectCallback();
      return user;
    } catch (error) {
      console.error('Complete login error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.userManager.signoutRedirect();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  async completeLogout(): Promise<void> {
    try {
      await this.userManager.signoutRedirectCallback();
    } catch (error) {
      console.error('Complete logout error:', error);
      throw error;
    }
  }

  async getUser(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser();
      if (user) {
        // Merge token claims into profile for easier access
        this.enrichUserProfile(user);
      }
      return user;
    } catch (error) {
      console.error('Get user error:', error);
      return null;
    }
  }

  /**
   * Enriches user profile with claims from the JWT token
   * This ensures roles and other claims are easily accessible
   */
  private enrichUserProfile(user: User): void {
    if (!user.profile) {
      return;
    }

    // Parse the access token to get claims directly from JWT
    const tokenClaims = this.parseJwtToken(user.access_token);
    
    console.log('Token claims from JWT:', tokenClaims);
    console.log('User profile before enrichment:', user.profile);
    
    // Extract roles from token claims
    // Try both standard 'role' claim and Microsoft-specific claim
    const roleClaim = tokenClaims?.role || 
                     tokenClaims?.[' http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
                     user.profile.role || 
                     user.profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    
    if (roleClaim) {
      user.profile.role = roleClaim;
      user.profile.roles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];
    }
    
    // Also copy other useful claims from token to profile
    if (tokenClaims) {
      if (tokenClaims.scope && !user.profile.scope) {
        user.profile.scope = tokenClaims.scope;
      }
      if (tokenClaims.client_id && !user.profile.client_id) {
        user.profile.client_id = tokenClaims.client_id;
      }
    }
    
    console.log('User roles after enrichment:', user.profile.role);
    console.log('Full user profile after enrichment:', user.profile);
  }

  /**
   * Parse JWT token to extract claims
   */
  private parseJwtToken(token: string): any {
    try {
      // JWT structure: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT token structure');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Base64Url decode
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  /**
   * Get user roles from the current user's profile
   * Returns an array of role strings
   */
  async getUserRoles(): Promise<string[]> {
    const user = await this.getUser();
    if (!user) {
      return [];
    }

    // Try to get roles from enriched profile first
    if (user.profile?.roles && Array.isArray(user.profile.roles)) {
      return user.profile.roles as string[];
    }

    // Fallback: Parse token directly if profile doesn't have roles
    const tokenClaims = this.parseJwtToken(user.access_token);
    if (tokenClaims) {
      const roleClaim = tokenClaims.role || 
                       tokenClaims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      
      if (roleClaim) {
        if (Array.isArray(roleClaim)) {
          return roleClaim as string[];
        }
        return typeof roleClaim === 'string' ? [roleClaim] : [];
      }
    }

    // Last resort: check profile directly
    const profileRoleClaim = user.profile?.role || 
                            user.profile?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    
    if (profileRoleClaim) {
      if (Array.isArray(profileRoleClaim)) {
        return profileRoleClaim as string[];
      }
      return typeof profileRoleClaim === 'string' ? [profileRoleClaim] : [];
    }

    return [];
  }

  /**
   * Check if the current user has a specific role
   */
  async hasRole(role: string): Promise<boolean> {
    const roles = await this.getUserRoles();
    return roles.includes(role);
  }

  /**
   * Check if the current user has any of the specified roles
   */
  async hasAnyRole(roles: string[]): Promise<boolean> {
    const userRoles = await this.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  /**
   * Check if the current user has all of the specified roles
   */
  async hasAllRoles(roles: string[]): Promise<boolean> {
    const userRoles = await this.getUserRoles();
    return roles.every(role => userRoles.includes(role));
  }

  /**
   * Get all claims from the access token for debugging
   */
  async getTokenClaims(): Promise<any> {
    const user = await this.getUser();
    if (!user?.access_token) {
      return null;
    }
    return this.parseJwtToken(user.access_token);
  }

  /**
   * Debug method to log all user information
   */
  async debugUserInfo(): Promise<void> {
    const user = await this.getUser();
    if (!user) {
      console.log('No user found');
      return;
    }

    console.group('🔍 User Debug Information');
    console.log('User Profile:', user.profile);
    console.log('Access Token Claims:', this.parseJwtToken(user.access_token));
    console.log('ID Token Claims:', user.id_token ? this.parseJwtToken(user.id_token) : 'No ID token');
    console.log('Token Expiration:', user.expires_at ? new Date(user.expires_at * 1000) : 'Unknown');
    console.log('Is Expired:', user.expired);
    console.log('Scopes:', user.scope);
    console.log('Roles from getUserRoles():', await this.getUserRoles());
    console.groupEnd();
  }

  async renewToken(): Promise<User | null> {
    try {
      const user = await this.userManager.signinSilent();
      return user;
    } catch (error) {
      console.error('Token renewal error:', error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return user !== null && !user.expired;
  }

  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.access_token || null;
  }

  async getIdToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.id_token || null;
  }

  // Event handlers (can be overridden)
  protected onUserLoaded(_user: User): void {
    // Override in subclass if needed
  }

  protected onUserUnloaded(): void {
    // Override in subclass if needed
  }

  protected onAccessTokenExpired(): void {
    // Attempt automatic renewal
    this.renewToken().catch(() => {
      // If renewal fails, redirect to login
      this.login();
    });
  }

  protected onAccessTokenExpiring(): void {
    // Attempt silent renewal before token expires
    void this.renewToken();
  }

  protected onSilentRenewError(_error: Error): void {
    // If silent renewal fails, might need to re-authenticate
    console.error('Silent renewal failed, user may need to re-authenticate');
  }

  protected onUserSignedOut(): void {
    // Clear any app-specific state
    sessionStorage.clear();
    localStorage.removeItem('tagna_authenticated');
    localStorage.removeItem('tagna_login_time');
  }

  // Utility methods
  getUserManager(): UserManager {
    return this.userManager;
  }

  getConfig(): OidcConfig {
    return this.config;
  }
}

// Export a singleton factory
let authServiceInstance: OidcAuthService | null = null;

export function initializeAuth(config: OidcConfig): OidcAuthService {
  if (!authServiceInstance) {
    authServiceInstance = new OidcAuthService(config);
  }
  return authServiceInstance;
}

export function getAuthService(): OidcAuthService | null {
  return authServiceInstance;
}